import {IFreePlayVoucherModel} from "./free-play-voucher.model.interface";
import { computed, observable, runInAction, makeObservable } from "mobx";
import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {Check, TimeSpan} from "@sparkware/app-utils";
import {FreePlayGameModel} from "../free-play-game/free-play-game.model";
import {NullableNumber, NullableString, NullableUndefinedDate} from "../../../../common/types/nullable-types";
import {FP_VOUCHER_TYPES} from "../../../../common/const/free_play_consts";
import {FreePlayVoucherData, UpdatedFreePlayVoucherData} from "../../firebase/topics/free-play/published-data";

export abstract class VoucherBaseModel implements IFreePlayVoucherModel {
    constructor(voucherData: FreePlayVoucherData,
                protected readonly services: IServiceFactoryExtended) {
        makeObservable(this);

        this._voucherData = voucherData;
    }

    abstract get voucherName(): string;
    abstract get voucherInGameTitle(): string;
    abstract get voucherDescription(): string;
    abstract get backgroundImageUrl(): string;
    abstract get iconUrl(): string;
    protected abstract _formatVoucherAmount(amount: number): string;


    @observable.ref
    protected _voucherData: FreePlayVoucherData;

    dispose() {
    }

    get id(): string {
        return this._voucherData.ID;
    }

    get freePlayType(): FP_VOUCHER_TYPES {
        return this._voucherData.FreePlayTypeId;
    }

    get isThirdPartyVoucher(): boolean {
        return Boolean(this._voucherData.CompanyID);
    }

    @computed
    get freePlayGames(): FreePlayGameModel[] {
        const gamesDictionary = this._voucherData.GameTypeIDList
            .map(gameType => this.services.games.gamesMap[gameType])
            .filter(gameInfo => Boolean(gameInfo))
            .toDictionary(game => game.gameType);



        const sortedGamesTypes = this.services.aiEngine.sortGames(Object.keys(gamesDictionary)
                                                                        .map(gt => parseInt(gt)));

        return sortedGamesTypes.map(gameType => new FreePlayGameModel(this, gamesDictionary[gameType]))
            .filter(fpg => {
                if(this.isThirdPartyVoucher) {
                    //for 3rd party vouchers we don't filter the games based on the voucher available amount and game minimum bet here.
                    //This is done in the VoucherForUiDisplayModel because we want to hide these only in UI but still be available on the open game flow
                    return true;
                } else {
                    //we do that only for 888 games
                    return fpg.hasEnoughBalanceToBet;
                }
            });
    }

    hasGame(gameType: number): boolean {
        return Boolean(this.freePlayGames.find(g => g.gameInfo.gameType === gameType));
    }

    get creationDate(): NullableUndefinedDate {
        return this._voucherData.CreationDate;
    }
    get expirationDate(): Date {
        return this._voucherData.ExpirationDate;
    }

    private get isExpired(): boolean {
        return this._voucherData.ExpirationDate.getTime() < Date.now();
    }

    get isValid(): boolean {
        return !this.isExpired && this.freePlayGames.length > 0;
    }

    updateVoucherData(newVoucherData: UpdatedFreePlayVoucherData): void {
        if(newVoucherData.ID !== this.id) {
            throw new Error('New voucher ID and existing voucher ID does not match');
        }
        runInAction(() => {
            this._voucherData = {
                ...this._voucherData,
                ...newVoucherData
            }
        });
    }

    get originalAmount(): NullableNumber {
        return this._voucherData.OriginAmount ?? null;
    }

    get currentBalance(): number {
        return this._voucherData.CurrentBalance ?? 0;
    }

    get formattedOriginalAmount(): NullableString {
        if(Check.isNullOrUndefined(this._voucherData.OriginAmount)) {
            return null;
        }

        return this._formatVoucherAmount(this._voucherData.OriginAmount);
    }

    get formattedCurrentBalance(): string {
        return this._formatVoucherAmount(this._voucherData.CurrentBalance);
    }

    get timeToExpiration(): TimeSpan {

        /*
         "FREE_PLAY_EXPIRES_IN_TIME": "Expires in {expiration}",
        "FREE_PLAY_EXPIRES_IN_EXPIRED": "Expired",
        "FREE_PLAY_EXPIRES_IN_DAY": "Expires in {expiration} days",
         */

        return TimeSpan.fromMilliseconds(this._voucherData.ExpirationDate.getTime() - this.services.time.now());
    }

    get isCloseToExpiration(): boolean {
        return this.timeToExpiration.totalDays <= 1;
    }

    get expirationMessage(): string {
        if(this.isExpired) {
            return this.services.translation.translate('FREE_PLAY_EXPIRES_IN_EXPIRED');
        }

        const timeToExpirationRoundedToSeconds = TimeSpan.fromSeconds(Math.floor(this.timeToExpiration.totalSeconds));

        if(this.isCloseToExpiration) {
            return this.services.translation.translate('FREE_PLAY_EXPIRES_IN_TIME', {expiration:timeToExpirationRoundedToSeconds.toString({useTotalHours: true})});
        }

        return this.services.translation.translate('FREE_PLAY_EXPIRES_IN_DAY', {expiration: Math.floor(timeToExpirationRoundedToSeconds.totalDays).toString()});
    }

    get companyId(): NullableNumber {
        return this._voucherData.CompanyID;
    }
}